import { memo, useMemo } from "react";
import { useTranslations } from "next-intl";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { type AiMessage } from "@/providers/AiAssistantProvider";
import { renderMarkdownLite } from "../renderMarkdownLite";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { MessageActions } from "./MessageActions";

/**
 * One user/assistant transcript bubble. Memoized: token streaming re-renders
 * the panel per network chunk, but patchLastAssistant preserves identity of
 * untouched messages, so memo keeps each chunk to a single bubble re-render
 * (§1a). Props stay primitives + stable callbacks — the parent computes
 * `canRetry`/`canRegenerate` so flipping `streaming` only changes the LAST
 * bubble's props.
 *
 * `variant="builder"` = tighter padding, no actions row, no Rive character.
 */
export const MessageBubble = memo(function MessageBubble({
  message,
  canRetry,
  onRetry,
  canRegenerate = false,
  variant = "chat",
  isLast = false,
  isLastAssistant = false,
}: {
  message: AiMessage;
  canRetry: boolean;
  onRetry: () => void;
  canRegenerate?: boolean;
  variant?: "chat" | "builder";
  /** True only for the final transcript message — gates the "stopped" marker
   *  so empty INTERMEDIATE assistant bubbles (left behind when the model
   *  chains tools with no text between them) render nothing instead of a
   *  misleading "Stopped". The genuine user-stop bubble is always last. */
  isLast?: boolean;
  /** True for the last ASSISTANT bubble, which may not be the last message —
   *  a turn can end on a tool row. Gates the price badge; `isLast` would hide
   *  it on exactly the turns that pause for an expensive confirmation. */
  isLastAssistant?: boolean;
}) {
  const t = useTranslations("aiAssistant");
  const builder = variant === "builder";
  const pad = builder ? "px-3 py-1.5" : "px-[13px] py-2.5";

  // Parse only when the text (or caret state) actually changed — memo blocks
  // parent-prop re-renders, but streaming/pin/suggestion flips still re-render
  // this bubble, and re-parsing the whole accumulated message each time is the
  // expensive part. Hook must precede the user-role early return.
  const caret = !!message.streaming && !!message.content;
  const renderedMarkdown = useMemo(
    () =>
      message.role === "assistant" && message.content
        ? renderMarkdownLite(message.content, { caret })
        : null,
    [message.role, message.content, caret],
  );

  if (message.role === "user") {
    const images = message.images ?? [];
    return (
      <div className="group flex flex-col items-end gap-1">
        {images.length > 0 && (
          <div className="flex max-w-[92%] flex-wrap justify-end gap-1.5">
            {images.map((img, i) => (
              <img
                // Read-only thumbnail of what the user sent — inert data URL
                // from the user's own File (no auto-link, no HTML render).
                key={i}
                src={`data:${img.mediaType};base64,${img.data}`}
                alt={t("imageAlt", { name: `${i + 1}` })}
                className="h-20 w-20 rounded-xl object-cover border outline-quaternary"
              />
            ))}
          </div>
        )}
        {message.content && (
          <div
            className={`max-w-[92%] rounded-xl rounded-br-[4px] cta-primary font-light-primary ${pad} text-sm leading-[1.6] whitespace-pre-wrap break-words`}
          >
            {message.content}
          </div>
        )}
        {!builder && message.content && (
          <MessageActions content={message.content} canRegenerate={false} onRegenerate={onRetry} />
        )}
      </div>
    );
  }

  const showThinking = message.streaming && !message.content && !message.errorCode;
  // Stop pressed before the first delta arrived — a quiet marker beats an empty bubble.
  const stoppedEmpty = !message.streaming && !message.content && !message.errorCode;
  // An empty, finalized, error-free assistant bubble that is NOT the last
  // message is always a tool-chaining artifact (the reducer opens a fresh
  // bubble after every tool_use); render nothing. Only the genuine user-stop
  // bubble — always the LAST message — keeps the "stopped" marker below.
  // What this user message cost. "1" is the baseline every user already
  // assumes — printing it under every reply is noise, not transparency.
  const creditCost = message.creditCost ?? 0;
  // A price shows once its total is FINAL. That is true of the last assistant
  // (nothing after it to change), of a restored row (it came back with its
  // message's final total), and of a settled one — a turn that ended earlier
  // in this same session. The third case is what stops a completed turn's cost
  // disappearing the moment the next question is asked and reappearing after a
  // reload; without it a mixed transcript reads `4 · 4 · nothing · 4`, and the
  // hole reads as "that one was free". An intermediate bubble mid-turn is none
  // of the three, because it can still be holding a partial total — the
  // provider enforces that by handing the price on at `turnContinue`, since
  // the running total is stamped on whichever bubble was open when each
  // credits frame arrived.
  const showCost =
    (isLastAssistant || !!message.restored || !!message.settled)
    && !message.streaming && creditCost > 1;
  // A turn that went straight to a tool with no preamble leaves the price on
  // an EMPTY bubble (the one turnStart opened). Collapsing it unconditionally
  // hid the price on exactly the turns worth showing one for — so an empty
  // bubble that carries a price renders the price alone, without the chrome.
  if (stoppedEmpty && !isLast) {
    if (!showCost) return null;
    return (
      <span className="font-tertiary text-xs tabular-nums">
        {t("turnCost", { count: creditCost })}
      </span>
    );
  }
  const showActions = !builder && !message.streaming && !!message.content;

  return (
    <div className="group flex flex-col items-start gap-1">
      {/* No whitespace-pre-wrap: renderMarkdownLite owns line structure. */}
      <div className={`max-w-[92%] rounded-xl rounded-bl-[4px] surface-secondary ${pad} text-sm leading-[1.6] break-words`}>
        {showThinking ? (
          <ThinkingIndicator character={!builder} />
        ) : stoppedEmpty ? (
          <span className="font-tertiary italic">{t("stopped")}</span>
        ) : (
          renderedMarkdown
        )}
        {message.errorCode && (
          <div
            role="alert"
            className={`flex items-start gap-1.5 text-sm font-error ${message.content ? "mt-1.5" : ""}`}
          >
            <IconAlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>
              {t.has(`errors.${message.errorCode}`)
                ? t(`errors.${message.errorCode}`)
                : t("errors.aiUnavailable")}
            </span>
          </div>
        )}
        {message.errorCode && canRetry && (
          <button
            className="mt-1.5 flex items-center gap-1 text-sm font-medium hover:underline cursor-pointer"
            onClick={onRetry}
          >
            <IconRefresh size={14} />
            {t("retry")}
          </button>
        )}
      </div>
      {showCost && (
        <span className="font-tertiary text-xs tabular-nums">
          {t("turnCost", { count: creditCost })}
        </span>
      )}
      {showActions && (
        <MessageActions
          content={message.content}
          canRegenerate={canRegenerate}
          onRegenerate={onRetry}
        />
      )}
    </div>
  );
});
